import {
  RFAConfigDTO,
  FloorConfigDTO,
  RiserDTO,
  FloorDTO,
  ApartmentDTO,
  ApartmentLabelDTO,
  BuildingDTO,
  AddressBookSorting,
  EntrancePlantDetailDTO,
  ApartmentInvitationDTO,
  ApartmentInvitationStatusEnum,
  LabelType,
  GateDTO,
  EPDeviceTypeEnum,
  AddressingType,
  PePlantConfig,
  RiserInterfaceDTO,
  RiserInterfaceMode,
} from '@/../client-generator/generated-clients/plants';
import i18n from '@/core/translations/i18n';
import { parse } from 'csv-parse';

const DEFAULT_NUMBER_OF_RISERS_IN_BUILDING = 2;
const DEFAULT_NUMBER_OF_FLOORS_IN_RISER = 2;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

interface RiserInterfaceFloorApartments {
  interface: RiserInterfaceDTO;
  floors: { floor: FloorDTO; apartments: ApartmentDTO[] }[];
}

interface RiserFloorsApartments {
  riser: RiserDTO;
  floors: { floor: FloorDTO; apartments: ApartmentDTO[] }[];
  interfaceTabs?: RiserInterfaceFloorApartments[];
}

enum CallMode {
  Address = 0,
  DirectCall = 1,
  Brfa = 2,
}

enum KeypadCodeValidation {
  OK = 0,
  KO_OnlyDigitsAllowed = 1,
  KO_TooLong = 2,
  KO_TooShort = 3,
  KO_SameDigitRepeated = 4,
  KO_ConsecutiveDigits = 5,
  KO_ConfirmationNotMatching = 6,
  KO_AlreadyUsed = 7,
}

enum ResidentialTypePlant {
  NEW_PLANT = 0,
  CONFIGURED_PLANT = 1,
}

enum RiserInterfaceModeCombo
{
  Mode5IndipendentRiser,
  Mode6ExtendedRiserAndOneMode7ExtendedRiserExpansion,
  Mode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion,
}

const addressBookSortingOptions = Object.keys(AddressBookSorting).reduce(
  (t: any, k: any) => {
    if (!isNaN(+k)) {
      t.push({
        name: i18n.global.t(`AppResourceEP.${AddressBookSorting[k]}`),
        code: +k,
      });
    }
    return t;
  },
  []
);

const noAppartmentOptions = (max: number, zero: boolean = false) => Array.from({ length: zero? max + 1 : max }, (_, index) => ({
  label: (index + (zero ? 0 : 1)).toString(),
  value: index + (zero ? 0 : 1),
}));

const distributeApartmentsToRaisersAndFloors = (structure: RFAConfigDTO) => {
  if (!structure.risers) return;
  if (structure.risers.length > 0) {
    var distribution = distributeElementsInGroups(
      structure.numberOfApartments!,
      structure.risers.length
    );
    for (let riser of structure.risers) {
      const riserIndex = structure.risers.indexOf(riser);
      const numberOfApartmentsInRiser = distribution[riserIndex];
      distributeApartmentsToFloors(riser.floors!, numberOfApartmentsInRiser);
    }
  } else {
    distributeApartmentsToFloors(
      structure.floorsWithoutRiser!,
      structure.numberOfApartments!
    );
  }
};

function distributeApartmentsToFloors(
  floors: FloorConfigDTO[],
  numberOfApartments: number
) {
  var distribution = distributeElementsInGroups(
    numberOfApartments,
    floors.length
  );
  for (let floor of floors) {
    var floorIndex = floors.indexOf(floor);
    floor.numberOfApartments = distribution[floorIndex];
  }
}

function distributeElementsInGroups(
  numberOfElements: number,
  numberOfGroups: number
): number[] {
  if (numberOfGroups === 0) return [numberOfElements];

  if (numberOfGroups < 0) throw new Error('Out of range');

  if (numberOfElements < 1)
    return Array.from({ length: numberOfGroups }, () => 0);

  const numberOfElementsInGroup = Math.floor(numberOfElements / numberOfGroups);
  const groupsWithExtraElement = numberOfElements % numberOfGroups;

  const distribution: number[] = [];
  for (let groupIndex = 0; groupIndex < numberOfGroups; groupIndex++) {
    const elementsInGroup =
      groupIndex < groupsWithExtraElement
        ? numberOfElementsInGroup + 1
        : numberOfElementsInGroup;
    distribution.push(elementsInGroup);
  }
  return distribution;
}

function getDeviceIconFromType(t: number | undefined): string {
  switch (t) {
    case 0:
      return 'entrancePlants/icon-new-pe';
    case 1:
      return 'entrancePlants/icon-keypad';
    case 2:
      return 'entrancePlants/icon-smart-reader';
    default:
      return '';
  }
}

function isCodeCorrectValue(code: string, codeRequiredLength: number = 5, alreadyUsedCodes: string[] = []) {
  const regex = new RegExp('^\\d+$');
  if (!regex.test(code)) return KeypadCodeValidation.KO_OnlyDigitsAllowed;
  
  if (code.length < codeRequiredLength) return KeypadCodeValidation.KO_TooShort;
  if (code.length > codeRequiredLength) return KeypadCodeValidation.KO_TooLong;

  const repeatedDigit = new RegExp(`(\\d)\\1{${codeRequiredLength - 1}}`);
  if (repeatedDigit.test(code)) return KeypadCodeValidation.KO_SameDigitRepeated;

  if (hasConsecutiveDigits(code)) return KeypadCodeValidation.KO_ConsecutiveDigits;
  
  if (alreadyUsedCodes.includes(code)) return KeypadCodeValidation.KO_AlreadyUsed;

  return KeypadCodeValidation.OK;
}

function hasConsecutiveDigits(code: string) {
  if(code.length < 2) return false;
  return "0123456789".includes(code) || "9876543210".includes(code);
}

const keypadCodeValidationToString = (value: KeypadCodeValidation) => {
  switch (value) {
    case KeypadCodeValidation.OK:
      return '';
    case KeypadCodeValidation.KO_OnlyDigitsAllowed:
      return i18n.global.t('entrancePlant.keypadCodeOnlyDigitsAllowed');
    case KeypadCodeValidation.KO_TooShort:
      return i18n.global.t('entrancePlant.keypadCodeTooShort');
    case KeypadCodeValidation.KO_TooLong:
      return i18n.global.t('entrancePlant.keypadCodeTooLong');
    case KeypadCodeValidation.KO_SameDigitRepeated:
      return i18n.global.t('entrancePlant.keypadCodeSameDigitRepeated');
    case KeypadCodeValidation.KO_ConsecutiveDigits:
      return i18n.global.t('entrancePlant.keypadCodeConsecutiveDigits');
    case KeypadCodeValidation.KO_AlreadyUsed:
      return i18n.global.t('AppResourceEP.KeypadCodeAlreadyExistsInPlant');
    default:
      return '';
  }
}

function loadRisersFloorsApartments(building: BuildingDTO, hasInterface: boolean = false): RiserFloorsApartments[] {
  if (!hasInterface) {
    return loadBRFAStructure(building);
  } else {
    return loadStructureWithInterface(building);
  }
}

const mapFloorWithApartments = (floor: FloorDTO, apartments: ApartmentDTO[]) => ({
  floor,
  apartments: apartments.filter(apt => apt.floorId === floor.floorId) ?? [],
});

const getLabelSortValue = (label?: ApartmentLabelDTO): string => {
  const surname = label?.surname?.trim() ?? '';
  return surname || label?.companyName?.trim() || '';
};

const compareLabelValues = (firstValue: string, secondValue: string): number => {
  const normalizedFirstValue = firstValue.toLowerCase();
  const normalizedSecondValue = secondValue.toLowerCase();

  if (normalizedFirstValue < normalizedSecondValue) return -1;
  if (normalizedFirstValue > normalizedSecondValue) return 1;
  return 0;
};

const sortApartmentLabels = (labels: ApartmentLabelDTO[] = []): ApartmentLabelDTO[] =>
  [...labels].sort((firstLabel, secondLabel) => {
    const firstValue = getLabelSortValue(firstLabel);
    const secondValue = getLabelSortValue(secondLabel);

    if (!firstValue && !secondValue) return 0;
    if (!firstValue) return -1;
    if (!secondValue) return 1;

    return compareLabelValues(firstValue, secondValue);
  });

const sortApartmentsByFirstLabel = (apartments: ApartmentDTO[] = []): ApartmentDTO[] =>
  [...apartments].sort((firstApartment, secondApartment) => {
    const firstValue = getLabelSortValue(sortApartmentLabels(firstApartment.labels)[0]);
    const secondValue = getLabelSortValue(sortApartmentLabels(secondApartment.labels)[0]);

    if (!firstValue && !secondValue) {
      return compareLabelValues(firstApartment.name ?? '', secondApartment.name ?? '');
    }
    if (!firstValue) return -1;
    if (!secondValue) return 1;

    return compareLabelValues(firstValue, secondValue);
  });

function loadBRFAStructure(building: BuildingDTO) {
  if (!building?.risers) return [];
  // Get floors not linked to any riser
  const floorsWithoutRiser = building.floors?.filter(floor => !floor.riserId) ?? [];

  // Handle buildings without risers but with floors
  if (building.risers.length === 0 && building.floors?.length) {
    return [{
      riser: new RiserDTO({ riserId: EMPTY_GUID, name: '' }),
      floors: building.floors.map(floor => mapFloorWithApartments(floor, building.apartments ?? [])),
    }];
  }

  const result: RiserFloorsApartments[] = [];

  // If there are floors not linked to any riser, add a fake riser first
  if (floorsWithoutRiser.length > 0) {
    result.push({
      riser: new RiserDTO({ riserId: EMPTY_GUID, name: building.name ?? '' }),
      floors: floorsWithoutRiser.map(floor => mapFloorWithApartments(floor, building.apartments ?? [])),
    });
  }

  // Add real risers with their linked floors
  building.risers.forEach(riser => {
    result.push({
      riser,
      floors: building.floors
        ?.filter(floor => floor.riserId === riser.riserId)
        .map(floor => mapFloorWithApartments(floor, building.apartments ?? [])) ?? [],
    });
  });

  return result;
}

function loadStructureWithInterface(building:BuildingDTO) {  
  const result: RiserFloorsApartments[] = [];
  
  if (building.backboneInterface) {
    const backboneApartments = building.apartments?.filter(
      apt => apt.riserInterfaceId === building.backboneInterface!.riserInterfaceId
    ) ?? [];
    const backboneFloors = building.floors?.filter(f => !f.riserId) ?? [];
    const floors = backboneFloors.length > 0
      ? backboneFloors.map(floor => mapFloorWithApartments(floor, backboneApartments))
      : [{ floor: new FloorDTO({ floorId: EMPTY_GUID, name: '' }), apartments: backboneApartments }];
    result.push({
      riser: new RiserDTO({ riserId: EMPTY_GUID, name: i18n.global.t('AppResourceEP.Backbone') ?? '' }),
      floors: [],
      interfaceTabs: [{
        interface: building.backboneInterface,
        floors,
      }],
    });
  }
  building.risers?.forEach(riser => {
    const riserInterfaces = riser.interfaces ?? [];
    const riserFloors = building.floors?.filter(f => f.riserId === riser.riserId) ?? [];
    const interfaceTabs: RiserInterfaceFloorApartments[] = riserInterfaces.map(iface => {
      const ifaceApartments = building.apartments?.filter(
        apt => apt.riserInterfaceId === iface.riserInterfaceId
      ) ?? [];
      const floors = riserFloors.length > 0
        ? riserFloors.map(floor => mapFloorWithApartments(floor, ifaceApartments))
        : [{ floor: new FloorDTO({ floorId: EMPTY_GUID, name: '' }), apartments: ifaceApartments }];
      return { interface: iface, floors };
    });
    result.push({
      riser,
      floors: [],
      interfaceTabs,
    });
  });

  return result;
  
}

function getAllKeypadCodes(plant: EntrancePlantDetailDTO): string[] {
  const keypadCodes: string[] = [];
  const buildingsKeycodes = getBuildingsKeypadCodes(plant);
  const apartmentsKeycodes = getApartmentsKeypadCodes(plant);
  const guestsKeycodes = getGuestsKeypadCodes(plant);
  const hiddenGuestsKeycodes = getHiddenGuestsKeypadCodes(plant);
  const plantKeycode = plant.keypadCodePasspartoutCode ?? '';

  return keypadCodes
    .concat(buildingsKeycodes, apartmentsKeycodes, guestsKeycodes, hiddenGuestsKeycodes, plantKeycode)
    .filter((code) => code !== '');
}

function getBuildingsKeypadCodes(plant: EntrancePlantDetailDTO): string[] {
  const buildingsCodes: string[] = [];
  plant.buildings?.forEach((building) => {
    if (building.keypadCodePasspartoutCode && building.keypadCodePasspartoutCode !== '') {
      buildingsCodes.push(building.keypadCodePasspartoutCode);
    }
  });
  return buildingsCodes;
}

function getApartmentsKeypadCodes(plant: EntrancePlantDetailDTO): string[] {
  const apartmentsCodes: string[] = [];
  plant.buildings?.forEach((building) => {
    building.apartments?.forEach((apartment) => {
      if (apartment.keypadCode && apartment.keypadCode !== '') {
        apartmentsCodes.push(apartment.keypadCode);
      }
    });
  });
  return apartmentsCodes;
}

function getGuestsKeypadCodes(plant: EntrancePlantDetailDTO): string[] {
  const guestsCodes: string[] = [];
  plant.gateGuests?.forEach((guest) => {
    if (guest.keypadCode && guest.keypadCode !== '') {
      guestsCodes.push(guest.keypadCode);
    }
  });
  return guestsCodes;
}

function getHiddenGuestsKeypadCodes(plant: EntrancePlantDetailDTO): string[] {
  const hiddenGuestsCodes: string[] = [];
  plant.hiddenGuests?.forEach((hGuest) => {
    if (hGuest.keyCode && hGuest.keyCode !== '') {
      hiddenGuestsCodes.push(hGuest.keyCode);
    }
  });
  return hiddenGuestsCodes;
}

interface SimpleLabel {
  id?: number;
  line1?: string;
  line2?: string;
  type?: LabelType;
  info?: string;
}

interface SingleLabelApartment {
  id?: number;
  buildingName?: string;
  apartmentName?: string;
  scsAddress?: string;
  callingCode?: string;
  label: SimpleLabel;
  badges: string[];
  keypadCode: string;
}

async function parseCsv(csvData: string, separator: string, addressingType: AddressingType): Promise<SingleLabelApartment[]> {
  return new Promise<SingleLabelApartment[]>((resolve, reject) => {
    const lista: SingleLabelApartment[] = [];
    
    parse(csvData, { delimiter: separator }, (err, data: any[]) => {
      if (err) return reject(err);
      try {
        validHeader(data[0], addressingType);

        data.shift(); // Remove header row
        
        for (const [index, line] of data.entries()) {
          const itemId = index + 2;
          
          const parsedLine = parseCsvLine(line, itemId, addressingType);
          if (parsedLine) {
            lista.push(parsedLine);
          }
        } 
      } catch (error) {
        return reject(error);
      }
      if (lista.length === 0) {
        return reject(new Error(i18n.global.t('importFile.error')));
      }
      
      resolve(lista);
    });
  });
}

function parseCsvLine(line: any[], itemId: number, addressingType: AddressingType): SingleLabelApartment | null {
  const trimmedLine = line.map((item: string) => item?.trim() || '');
  const supportsCallingCode = addressingType === AddressingType.DirectCall_Alphanumeric || addressingType === AddressingType.DirectCall_NumbersOnly;
  let buildingName: string, apartmentName: string, scsAddress: string, callingCode: string;
  let labelTypeStr: string, line1: string, line2: string, info: string, badgesStr: string, keypadCode: string;
  
  if (supportsCallingCode) {
    [buildingName, apartmentName, scsAddress, callingCode, labelTypeStr, line1, line2, info, badgesStr, keypadCode] = trimmedLine;
  } else {
    [buildingName, apartmentName, scsAddress, labelTypeStr, line1, line2, info, badgesStr, keypadCode] = trimmedLine;
    callingCode = '';
  }
  
  // Validate mandatory fields
  validateMandatoryFields(buildingName, apartmentName, scsAddress, itemId);
  
  if (supportsCallingCode) {
    validateCallingCode(callingCode, addressingType, itemId);
  }
  
  // Validate and parse label type
  const labelType = validateAndParseLabelType(labelTypeStr, itemId);
  
  // Validate label length
  validateLabelLength(line1, line2, labelType, itemId);
  
  // Validate info length
  if (info.length > 255) {
    throw new Error(`${i18n.global.t('import.errorInfoFieldLength')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
  
  // Parse and validate badges
  const badges = parseBadges(badgesStr, itemId);
  
  // Validate keypad code
  const validatedKeypadCode = validateKeypadCode(keypadCode, itemId);
  
  // Only add if at least one field has content
  if ((supportsCallingCode && callingCode !== '') || [line1, line2, keypadCode].some(field => field !== '') || badges.length > 0) {
    return {
      id: itemId,
      buildingName,
      apartmentName,
      scsAddress,
      callingCode: supportsCallingCode ? callingCode : undefined,
      label: { id: itemId, type: labelType, line1, line2, info },
      badges,
      keypadCode: validatedKeypadCode,
    };
  }
  
  return null;
}

function validateMandatoryFields(buildingName: string, apartmentName: string, scsAddress: string, itemId: number): void {
  if (!buildingName || !apartmentName || !scsAddress) {
    throw new Error(`${i18n.global.t('import.mandatoryFields')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
}

function validateCallingCode(callingCode: string, addressingType: AddressingType, itemId: number): void {

  const callingCodePattern = addressingType === AddressingType.DirectCall_Alphanumeric ? /^[a-zA-Z0-9]{1,9}$/ : /^\d{1,9}$/;

  if (!callingCode || !callingCodePattern.test(callingCode)) {
    const errorString = addressingType === AddressingType.DirectCall_Alphanumeric 
      ? i18n.global.t('import.errorCallingCodeAlphanumericFormat')
      : i18n.global.t('import.errorCallingCodeNumericFormat');
    throw new Error(`${errorString} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
}

function validateAndParseLabelType(labelTypeStr: string, itemId: number): LabelType {
  const normalizedType = labelTypeStr.toLowerCase();
  
  if (normalizedType !== 'business' && normalizedType !== 'private') {
    throw new Error(`${i18n.global.t('import.errorLabelType')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
  
  return normalizedType === 'business' ? LabelType.Business : LabelType.Private;
}

function validateLabelLength(line1: string, line2: string, labelType: LabelType, itemId: number): void {
  const labelLength = labelType === LabelType.Business ? 40 : 25;
  
  if (line1.length > labelLength || line2.length > labelLength) {
    throw new Error(`${i18n.global.t('import.errorLabelsFieldsLength', { length: labelLength })} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
}

function parseBadges(badgesStr: string, itemId: number): string[] {
  const badges = badgesStr
    .split(',')
    .map(badge => badge.toUpperCase().trim())
    .filter(badge => badge !== '');
  
  if (badges.length > 10) {
    throw new Error(`${i18n.global.t('import.errorBadgesFieldLength')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
  
  if (badges.some(badge => !/^[A-F0-9]{8}$/.test(badge))) {
    throw new Error(`${i18n.global.t('import.errorBadgesFieldFormat')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
  
  return badges;
}

function validateKeypadCode(keypadCode: string, itemId: number): string {
  if (keypadCode === '') return '';
  
  const isNumeric = !isNaN(Number(keypadCode));
  const hasValidLength = keypadCode.length >= 4 && keypadCode.length <= 9;
  const isValid = isNumeric && hasValidLength;
  
  if (!isValid && !keypadCode.includes('#')) {
    throw new Error(`${i18n.global.t('import.errorKeypadCodeFormat')} | ${i18n.global.t('import.errorLine', { line: itemId })}`);
  }
  
  return isValid ? keypadCode : '';
}

const validHeader = (header: any[], addressingType: AddressingType) => {
  const supportsCallingCode = addressingType === AddressingType.DirectCall_Alphanumeric || addressingType === AddressingType.DirectCall_NumbersOnly;
  
  const expectedHeaders = supportsCallingCode 
    ? ['Building Name', 'Apartment Name', 'SCS N address', 'Calling Code', 'Label Type', 'Line1', 'Line2', 'Info', 'Badges', 'Keypad Code']
    : ['Building Name', 'Apartment Name', 'SCS N address', 'Label Type', 'Line1', 'Line2', 'Info', 'Badges', 'Keypad Code'];
  
  // Normalize headers to handle optional "(One for apartment)" suffix
  const normalizedHeader = header.map((h: string) => h.replace(/\s*\(One for apartment\)\s*$/i, '').trim());
  
  const missingColumns: string[] = [];
  const wrongOrderColumns: string[] = [];
  
  for (let i = 0; i < expectedHeaders.length; i++) {
    const expectedHeader = expectedHeaders[i];
    
    if (!normalizedHeader.includes(expectedHeader)) {
      missingColumns.push(expectedHeader);
    } else if (normalizedHeader[i] !== expectedHeader) {
      wrongOrderColumns.push(expectedHeader);
    }
  }
  
  if (missingColumns.length > 0) {
    console.log('Missing columns:', missingColumns);
    throw new Error(i18n.global.t('import.csvMissingColumns', { columns: missingColumns.join(', ') }));
  }
  
  if (wrongOrderColumns.length > 0) {
    throw new Error(i18n.global.t('import.csvWrongColumnsOrder', { columnsOrder: expectedHeaders.join(', ') }));
  }
}

function fileIsCsv(file: File) {
  return (
    (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') &&
    file.name.endsWith('.csv')
  );
}

async function processCsvData(
  csvData: string,
  addressingType: AddressingType,
): Promise<SingleLabelApartment[]> {
    const firstRow = csvData.substring(0 , csvData.indexOf('\n'));
    const separator = firstRow.includes(',') ? ',' : ';';
    const lista = await parseCsv(csvData, separator, addressingType);
    return lista;
}

async function processCsvDataMassiveInvitation(
  csvData: string,
  currentEntrancePlant: EntrancePlantDetailDTO
): Promise<ApartmentInvitationDTO[]> {
  try {
    const firstRow = csvData.substring(0 , csvData.indexOf('\n'));
    const separator = firstRow.includes(',') ? ',' : ';';
    const lista = await parseCsvMassiveInvitation(
      csvData,
      currentEntrancePlant,
      separator
    );
    return lista;
  } catch (error) {
    return [];
  }
}

async function parseCsvMassiveInvitation(
  csvData: string,
  currentEntrancePlant: EntrancePlantDetailDTO,
  separator: string
): Promise<ApartmentInvitationDTO[]> {
  return new Promise((resolve, reject) => {
    const lista: ApartmentInvitationDTO[] = [];
    parse(csvData, { delimiter: separator }, function (err, data: any[]) {
      if (err) {
        reject(err);
      } else {
        data.forEach((line: any[]) => {
          const buildingName = line[0]?.trim();
          const apartmentName = line[1]?.trim();
          const email = line[3]?.trim();

          const building = currentEntrancePlant.buildings?.find(
            (building) => building.name === buildingName
          );

          if (building) {
            let apartment: ApartmentDTO = {} as ApartmentDTO;

            building.apartments?.forEach((a) => {
              if (a.name === apartmentName) {
                apartment = a;
              }
            });

            if (apartment && apartment.canInvite) {
              lista.push(
                new ApartmentInvitationDTO({
                  apartmentId: apartment.apartmentId,
                  email: email,
                  status: ApartmentInvitationStatusEnum.Pending,
                })
              );
            }
          }
        });
        resolve(lista);
      }
    });
  });
}

function getReferenceDeviceForVirtualDevice(gate: GateDTO)
{
    if (!gate.devices) return null;
    if (gate.devices.length == 1) {
      return gate.devices[0];
    } else if (gate.devices.length == 2) {
      return gate.devices.find(d => d.type == EPDeviceTypeEnum.Linea5000) ?? null;
    } else {
      return null;
    }
}

  function isUniqueLinkingActive(pePlantConfig: PePlantConfig, hasBackboneApartments: boolean): boolean {
    let result = false;

    const risers = pePlantConfig.risers;
    let backboneInterface = null;
    if (hasBackboneApartments) {
      backboneInterface = pePlantConfig.interfaces?.find(i =>
        i.isBackboneInterface
      );
    }
    const orderedRisers = risers?.sort((a, b) => a.index! - b.index!);
    console.log(orderedRisers)
    const buildings = pePlantConfig.buildings?.sort((a, b) => a.index! - b.index!);
    for (let i = 0; i < (buildings?.length ?? 0); i++) {
      const building = buildings?.[i];
      if (backboneInterface && i === 0) {
        result = backboneInterface.peBuildingConfigId === building?.id;
      } else {
        const riser = orderedRisers?.[backboneInterface ? i - 1 : i];
        result = riser?.peBuildingConfigId === building?.id;
        console.log()
        console.log(`Building ${building?.id}`);
        console.log(`Riser ${riser?.id}`);
        console.log(`Riser linked to building ${riser?.peBuildingConfigId}`);
        console.log(`Is unique linking for this building: ${result}`);
      }
      if (!result) {
        break;
      } 
    }

    return result
  }

  function getInterfaceModeLabel(mode: RiserInterfaceMode | undefined): string {
    if (mode === undefined) return '';
    switch (mode) {
      case RiserInterfaceMode.Mode5IndipendentRiser:
        return i18n.global.t('AppResourceEP.ShortMode5IndependentRiser') as string;
      case RiserInterfaceMode.Mode6ExtendedRiser:
        return i18n.global.t('AppResourceEP.ShortMode6ExtendedRiser') as string;
      case RiserInterfaceMode.Mode7ExtendedRiserExpansion:
        return i18n.global.t('AppResourceEP.ShortMode7ExtendedRiserExpansion') as string;
    }
  }

export {
  distributeApartmentsToRaisersAndFloors,
  DEFAULT_NUMBER_OF_RISERS_IN_BUILDING,
  getReferenceDeviceForVirtualDevice,
  DEFAULT_NUMBER_OF_FLOORS_IN_RISER,
  processCsvDataMassiveInvitation,
  RiserInterfaceFloorApartments,
  keypadCodeValidationToString,
  loadRisersFloorsApartments,
  getLabelSortValue,
  sortApartmentLabels,
  sortApartmentsByFirstLabel,
  addressBookSortingOptions,
  RiserInterfaceModeCombo,
  getBuildingsKeypadCodes,
  getInterfaceModeLabel,
  getDeviceIconFromType,
  isUniqueLinkingActive,
  RiserFloorsApartments,
  KeypadCodeValidation,
  SingleLabelApartment,
  ResidentialTypePlant,
  getGuestsKeypadCodes,
  noAppartmentOptions,
  isCodeCorrectValue,
  getAllKeypadCodes,
  processCsvData,
  SimpleLabel,
  EMPTY_GUID,
  fileIsCsv,
  CallMode,
};